import React from "react";
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { t } from "ttag";
import {
  entityHasMissingInCloud,
  entityHasMissingOnDevice,
  entitySideFetchState,
  resolveEntityStatus
} from "./deviceSyncCompare";
import {
  ALL_SYNC_ENTITIES,
  getEntityLabel,
  getUnsupportedSyncReason,
  SyncAction,
  syncButtonDisableReason
} from "./deviceSyncConfig";
import { withButtonTooltip } from "./ButtonTooltip";
import { FetchStateIcon, StatusChip } from "./SyncStatusIndicators";
import useStyles from "./DeviceSync.style";

const SyncEntityTable = ({
  entities,
  currentEntity,
  loadingCompare,
  deviceConnected,
  syncing,
  onSelectEntity,
  onRequestSync
}: {
  entities: Record<string, any>;
  currentEntity: string;
  loadingCompare: boolean;
  deviceConnected: boolean;
  syncing: boolean;
  onSelectEntity: (entity: string) => void;
  onRequestSync: (entity: string, action: SyncAction) => void;
}) => {
  const classes = useStyles();

  return (
    <Paper className={classes.section}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t`Sync per entity`}
      </Typography>
      <TableContainer className={classes.entityTableWrap}>
      <Table size="small" className={classes.entityTable}>
        <TableHead>
          <TableRow className={classes.tableHeadRow}>
            <TableCell className={classes.th}>{t`Entity`}</TableCell>
            <TableCell className={classes.th}>{t`Status`}</TableCell>
            <TableCell className={classes.th}>📱 {t`Device`}</TableCell>
            <TableCell className={classes.th}>☁️ {t`Cloud`}</TableCell>
            <TableCell className={classes.th}>📱➡️☁️ {t`Device → cloud`}</TableCell>
            <TableCell className={classes.th}>☁️➡️📱 {t`Cloud → device`}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ALL_SYNC_ENTITIES.map((entity) => {
            const result = entities[entity];
            const displayStatus = resolveEntityStatus(result);
            const pushBlocked = getUnsupportedSyncReason(entity, "push");
            const pullBlocked = getUnsupportedSyncReason(entity, "pull");
            const alreadyMatches = displayStatus === "pass";
            const buttonStatus = loadingCompare ? "pending" : displayStatus;
            const pushDisabledReason = syncButtonDisableReason(
              entity,
              "push",
              buttonStatus,
              deviceConnected
            );
            const pullDisabledReason = syncButtonDisableReason(
              entity,
              "pull",
              buttonStatus,
              deviceConnected
            );
            const suggestPush =
              !alreadyMatches && !pushBlocked && entityHasMissingOnDevice(result);
            const suggestPull =
              !alreadyMatches && !pullBlocked && entityHasMissingInCloud(result);
            return (
              <TableRow key={entity} selected={entity === currentEntity}>
                <TableCell className={classes.td}>
                  <Button
                    className={classes.entityNameButton}
                    onClick={() => onSelectEntity(entity)}
                  >
                    {getEntityLabel(entity)}
                  </Button>
                </TableCell>
                <TableCell className={classes.td}>
                  <StatusChip status={displayStatus} />
                </TableCell>
                <TableCell className={classes.td}>
                  <FetchStateIcon state={entitySideFetchState(result, "device")} />
                </TableCell>
                <TableCell className={classes.td}>
                  <FetchStateIcon state={entitySideFetchState(result, "cloud")} />
                </TableCell>
                <TableCell className={classes.td}>
                  {!pullBlocked && withButtonTooltip(
                    pullDisabledReason
                      || (suggestPull
                        ? t`Suggestion: missing in cloud — pull device → cloud`
                        : undefined),
                    <Button
                      className={suggestPull ? classes.recommendedButton : classes.directionButton}
                      size="small"
                      variant={suggestPull ? "contained" : "outlined"}
                      color={suggestPull ? "primary" : "inherit"}
                      disabled={!!pullDisabledReason || syncing}
                      startIcon={<ArrowForward fontSize="small" />}
                      onClick={() => onRequestSync(entity, "pull")}
                    >
                      {t`Pull`}
                    </Button>
                  )}
                </TableCell>
                <TableCell className={classes.td}>
                  {!pushBlocked && withButtonTooltip(
                    pushDisabledReason
                      || (suggestPush
                        ? t`Suggestion: missing on device — push cloud → device`
                        : undefined),
                    <Button
                      className={suggestPush ? classes.recommendedButton : classes.directionButton}
                      size="small"
                      variant={suggestPush ? "contained" : "outlined"}
                      color={suggestPush ? "primary" : "inherit"}
                      disabled={!!pushDisabledReason || syncing}
                      startIcon={<ArrowBack fontSize="small" />}
                      onClick={() => onRequestSync(entity, "push")}
                    >
                      {t`Push`}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </TableContainer>
    </Paper>
  );
};

export default SyncEntityTable;
